import { BackToListButton } from '@/features/missions/components/BackToListButton'
import { MissionBadge } from '@/features/missions/components/MissionBadge'
import { MissionNotAvailable } from '@/features/missions/components/MissionNotAvailable'
import { useMissionsUrl } from '@/features/missions/context/MissionsUrlProvider'
import { Button, Spinner, Text } from '@/ui-kit'
import { formatNumber } from '@/utils'
import { useQueryClient } from '@tanstack/react-query'
import { Star } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useNectarProject } from '../api'
import { NECTAR_TYPE_TEXT_MAP, NectarEnrollmentStatus } from '../api/models'
import { EnrollmentContent } from './EnrollmentContent'
import { MissionProjectHeader } from './MissionProjectHeader'
import { NectarTaskContent } from './NectarTaskContent'
import { PROJECT_FLOW_COMPONENTS } from './project-flows'

// Side-effect imports: each module self-registers in PROJECT_FLOW_COMPONENTS
import './voice-conversation/VoiceConversationFlow'
import './proficiency-assessment/ProficiencyAssessmentFlow'

const STATUS_BADGE_TEXT: Record<NectarEnrollmentStatus, string> = {
  IN_PROGRESS: 'In Progress',
  PENDING_REVIEW: 'Under Review',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  EXPIRED: 'Expired'
}

export const NectarTaskPage = ({ projectId }: { projectId: string }) => {
  const { missionsUrl } = useMissionsUrl()

  const { data: project, isFetching: isFetchingProject } = useNectarProject(projectId)
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  if (isFetchingProject) {
    return (
      <div className="flex w-full justify-center py-6">
        <Spinner className="mt-8" />
      </div>
    )
  }

  if (!project) {
    return (
      <div className="flex flex-col items-center justify-center py-8">
        <Text type="h3" className="mb-4">
          Project not found
        </Text>
        <Button onClick={() => navigate(missionsUrl)}>Back to Missions</Button>
      </div>
    )
  }

  const enrollmentStatus = project.enrollment?.status
  const isTerminal =
    enrollmentStatus === 'APPROVED' || enrollmentStatus === 'REJECTED' || enrollmentStatus === 'EXPIRED'

  return (
    <>
      <div className="mb-10 flex flex-col gap-6 py-8">
        <div className="flex flex-wrap justify-between gap-1 gap-y-3">
          <BackToListButton href={missionsUrl} />
          <div className="ml-auto flex gap-1">
            <MissionBadge variant="success">
              {project.enrollment ? STATUS_BADGE_TEXT[project.enrollment!.status] : 'New'}
            </MissionBadge>
            <MissionBadge variant="default">{NECTAR_TYPE_TEXT_MAP[project.type] ?? project.type}</MissionBadge>
            {!!project.rewardConfig?.amount && (
              <MissionBadge variant="success">
                <Star size={15} /> {formatNumber(project.rewardConfig.amount, 0)} points
              </MissionBadge>
            )}
          </div>
        </div>
        {!project.availability.available ? (
          <>
            <MissionProjectHeader project={project} />
            <MissionNotAvailable reason={project.availability.reason ?? 'unavailable'} />
          </>
        ) : project.enrolled || isTerminal ? (
          <>
            <Text type="h4" className="-mb-2">
              {project?.name}
            </Text>
            {(() => {
              const FlowComponent = PROJECT_FLOW_COMPONENTS.get(project.type)
              if (FlowComponent) {
                return (
                  <FlowComponent
                    projectId={project.id}
                    projectName={project.name}
                    language={project.language}
                    pointsAmount={project.rewardConfig?.amount}
                    onBack={() => navigate(missionsUrl)}
                    onCompleted={() => {
                      queryClient.invalidateQueries({ queryKey: ['NECTAR_PROJECT', project.id] })
                      queryClient.invalidateQueries({ queryKey: ['NECTAR_PROJECTS'] })
                    }}
                  />
                )
              }
              return <NectarTaskContent project={project} />
            })()}
          </>
        ) : (
          <EnrollmentContent project={project} />
        )}
      </div>
    </>
  )
}
